import React from 'react';
import { useRouter } from 'next/router';
import NextLink from 'components/next-link';
import { Icon } from 'components/icon/icon';
import { Button } from 'components/button';
import IconChevronRight from 'public/assets/icons/chevron-right.svg';
import { getFormattedDate } from 'utils/date';
import { getBreadCrumb } from './search-result-breadcrumb';
import styles from './search-overview.module.scss';

interface SearchResultCardProps {
	item: {
		title?: string;
		name?: string;
		description?: string | React.ReactNode;
		date?: Date | string;
		publishedAtUnix?: number;
		url?: string;
		link?: {
			linkUrl?: string;
		};
	};
	link: string | null | undefined;
	href: string;
	index: number;
	formatMessage: (message: { id: string }, values?: Record<string, string>) => string;
}

export const SearchResultCard: React.FC<SearchResultCardProps> = ({ item, link, href, index, formatMessage }) => {
	const { locale } = useRouter();
	const title = item.title || item.name || '';
	const isInternalLink = !!link;
	const date = item.date || (item.publishedAtUnix ? item.publishedAtUnix * 1000 : undefined);

	const externalLinkAriaLabel = formatMessage(
		{ id: 'modules.searchOverview.externalLinkNavigate' },
		{ pageName: title }
	);

	return (
		<div>
			{/* Desktop Card */}
			<div className={styles.card}>
				{isInternalLink ? (
					<NextLink href={href} passHref>
						<div className={styles.titleHeader}>
							<span className={styles.icon}>
								<Icon customIcon={<IconChevronRight />} size="12px" />
							</span>
							<span className={styles.title}>{title}</span>
						</div>
					</NextLink>
				) : (
					<a href={href} target="_blank" rel="noreferrer" aria-label={externalLinkAriaLabel}>
						<div className={styles.titleHeader}>
							<span className={styles.icon}>
								<Icon customIcon={<IconChevronRight />} size="12px" />
							</span>
							<span className={styles.title}>{title}</span>
						</div>
					</a>
				)}
				<div className={styles.description}>{item.description}</div>
				<div className={styles.bottomRow}>
					{isInternalLink ? (
						getBreadCrumb(link, index)
					) : (
						<div className={styles.breadCrumb}>
							<Button
								variant="tertiary-dark"
								title={formatMessage({ id: 'modules.searchOverview.external' })}
								ariaLabel={externalLinkAriaLabel}
								href={item.url || item.link?.linkUrl}
								icon={<Icon reactIcon="MdOpenInNew" size="12px" />}
								iconPosition="right"
								targetBlank
							/>
						</div>
					)}
					{date && <div className={styles.date}>{getFormattedDate(new Date(date), 'dd MMMM yyyy', locale)}</div>}
				</div>
			</div>

			{/* Mobile Card */}
			{isInternalLink && link && (
				<NextLink href={link} passHref>
					<div className={styles.mobileCard}>
						<div className={styles.titleHeader}>
							<div className={styles.icon}>
								<Icon customIcon={<IconChevronRight />} size="12px" />
							</div>
							<span className={styles.title}>{title}</span>
						</div>
						<div className={styles.description}>{item.description}</div>
						{date && <div className={styles.date}>{getFormattedDate(new Date(date), 'dd MMMM yyyy', locale)}</div>}
					</div>
				</NextLink>
			)}
		</div>
	);
};
